import {
  buildDirectory,
  DIRECTORY_SNAPSHOT_PATH,
  parseDirectorySnapshot,
  type DirectoryEntry,
} from "@/lib/brokerageDirectory";
import { fetchPublicOrganizationProfile, fetchPublicOrganizationSlugs } from "@/lib/publicOrganizationRpc";

/**
 * Loads the greater Sacramento directory in the browser.
 *
 * 1. The build's snapshot first (`/brokerages/sacramento/directory.json`,
 *    written by scripts/prerender.mjs from the same profiles it prerendered).
 *    One small static request, the same data the prerendered HTML shows, and
 *    as current as the organization pages themselves (both follow a deploy).
 * 2. When there is no snapshot (the dev server, or a build that could not
 *    reach the database), the live anonymous RPCs: the index, then each
 *    organization's public profile, a few at a time.
 *
 * A failure in the live path THROWS, so the page renders its error state; it
 * never resolves to an empty list that would read as "no brokerages".
 */

export const BROKERAGE_DIRECTORY_QUERY_KEY = ["brokerage-directory", "sacramento"] as const;

const PROFILE_CONCURRENCY = 6;

/**
 * The live fallback reads one public profile per organization nationwide, so
 * it stops above this many and the page shows its error state instead of
 * fanning out. Normal use reads the build snapshot; this only bounds the
 * fallback (a build that could not reach the database, or the dev server).
 */
export const MAX_LIVE_PROFILES = 300;

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** The snapshot's entries, or null when there is no usable snapshot. Never throws. */
export async function loadDirectorySnapshot(fetchImpl: FetchLike = fetch): Promise<DirectoryEntry[] | null> {
  try {
    const res = await fetchImpl(DIRECTORY_SNAPSHOT_PATH, { headers: { Accept: "application/json" } });
    // An unmatched path is rewritten to the SPA shell (200, text/html), so the
    // content type, not only the status, says whether this is the snapshot.
    if (!res.ok || !(res.headers.get("content-type") ?? "").includes("json")) return null;
    return parseDirectorySnapshot(await res.json());
  } catch {
    return null;
  }
}

/** The directory from the live public RPCs. Throws on any failed read. */
export async function loadDirectoryLive(): Promise<DirectoryEntry[]> {
  const slugs = await fetchPublicOrganizationSlugs();
  if (slugs.length > MAX_LIVE_PROFILES) {
    throw new Error(`brokerage directory: ${slugs.length} public organizations exceed the live fallback limit`);
  }
  const profiles: Awaited<ReturnType<typeof fetchPublicOrganizationProfile>>[] = [];
  for (let i = 0; i < slugs.length; i += PROFILE_CONCURRENCY) {
    const batch = slugs.slice(i, i + PROFILE_CONCURRENCY);
    // A null profile (unpublished between the two reads) is skipped by
    // buildDirectory; a rejected one fails the whole load.
    profiles.push(...(await Promise.all(batch.map((slug) => fetchPublicOrganizationProfile(slug)))));
  }
  return buildDirectory(profiles);
}

export async function loadBrokerageDirectory(fetchImpl: FetchLike = fetch): Promise<DirectoryEntry[]> {
  const snapshot = await loadDirectorySnapshot(fetchImpl);
  if (snapshot) return snapshot;
  return loadDirectoryLive();
}
